import type React from 'react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import isEmpty from 'lodash/isEmpty'
import { getWindow, hasWindow } from '@ztechapps/common.utils'
import { computeFlipDeltas } from './computeFlipDeltas'
import type {
  DragSortContainerProps,
  DragSortHandleProps,
  DragSortItemProps,
  UseDragSortProps,
  UseDragSortResult,
} from './types'

const DEFAULT_ANIMATION_MS = 150

const noOpRef = (): void => {
  /* no-op */
}

const noOpHandler = (): void => {
  /* no-op */
}

const readIndexFromEvent = (e: React.DragEvent<HTMLElement>): number | null => {
  const raw = e.currentTarget?.dataset?.dragSortIndex
  if (raw === undefined || raw === null || raw === '') {
    return null
  }
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * `useDragSort` — native HTML5 drag-and-drop sorting primitive.
 *
 * A stateless-w.r.t.-list hook that wires the native HTML5 DnD handlers,
 * tracks drag / drop indices, and runs a FLIP reorder animation — all with
 * zero external dependencies.
 *
 * ### Why this hook lives in `libs/webui/util/`, not `libs/common/hook/`
 *
 * Despite being a React hook, `useDragSort` is tightly coupled to the
 * browser DOM — it reads `DragEvent`, `getBoundingClientRect`,
 * `matchMedia`, `requestAnimationFrame`, and `setTimeout`, and wires the
 * six native HTML5 DnD event handlers. The `libs/common/hook` project is
 * tagged `type:common` and is meant to be platform-agnostic (safe to
 * import from Node.js / SSR / backend). A DOM-heavy hook would violate
 * that semantic boundary. Browser-only hooks live in `libs/webui/util`
 * alongside other webui primitives like `drag-handle`.
 *
 * ### Two prop-getters
 *
 * - `getItemProps(index)` — spread on the host element (row / card / list
 *   item). Provides ref (for FLIP measurement), visual-feedback data
 *   attributes, and drop-side handlers so the host is a valid drop target.
 * - `getHandleProps(index)` — spread on the actual drag handle element
 *   (the part the user grabs). Provides `draggable=true` and the drag-
 *   source handlers (`onDragStart` / `onDragEnd`). When the host IS the
 *   handle, spread BOTH on the same element.
 *
 * This split is required because native HTML5 DnD will not initiate a
 * drag from inside an interactive child like `<button>` or `<a>` — the
 * handle element itself must be `draggable=true`.
 *
 * ### Reorder semantics are the caller's responsibility
 *
 * The hook itself does NOT compute a reordered copy of `items`. It emits
 * `onSort({ oldIndex, newIndex })` and leaves the mutation to the caller.
 * Most consumers use the splice-after-remove insert pattern:
 *
 * ```ts
 * const next = [...items]
 * const [moved] = next.splice(oldIndex, 1)
 * next.splice(newIndex, 0, moved)
 * setItems(next)
 * ```
 *
 * Custom swap semantics can be implemented inline in `onSort` if needed.
 *
 * ### FLIP pipeline (First → Last → Invert → Play)
 *
 * 1. **First** — on `drop`, before calling `onSort`, walk every tracked
 *    element and snapshot its `getBoundingClientRect()` keyed by
 *    `getItemKey`.
 * 2. **Last** — after React re-renders the new order, a
 *    `useLayoutEffect` keyed on the item-key sequence measures each
 *    element's new rect.
 * 3. **Invert** — for each item whose rect changed, synchronously apply
 *    `transform: translate(dx, dy)` with `transition: none`, placing the
 *    element back at its pre-reorder position before the browser paints.
 * 4. **Play** — on the next `requestAnimationFrame`, apply
 *    `transition: transform ${animationMs}ms ease` and clear the
 *    transform, letting the browser animate the element from the inverted
 *    position to its final spot.
 *
 * Inline styles are cleared via `setTimeout(animationMs)` after playback.
 *
 * ### `getItemKey` stability
 *
 * `getItemKey` is required because array indices are NOT stable across
 * reorders. The hook uses these keys to correlate rects between the
 * First and Last snapshots. Typical implementation: `(item) => item._id`.
 *
 * ### Reduced-motion and `animationMs: 0`
 *
 * If the user's environment reports `prefers-reduced-motion: reduce`, the
 * entire FLIP pipeline is skipped and items snap into place. Same result
 * if the caller passes `animationMs: 0`. The media query is consulted via
 * `hasWindow()` from `@ztechapps/common.utils` — never bare `window`.
 */
export const useDragSort = <T,>(props: UseDragSortProps<T>): UseDragSortResult => {
  const { items, getItemKey, onSort, animationMs = DEFAULT_ANIMATION_MS, disabled = false } = props

  const [draggedIndex, setDraggedIndexState] = useState<number | null>(null)
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null)
  // Which half of the target row the cursor is over — 'before' means the
  // top half ("insert above this row"), 'after' means the bottom half
  // ("insert below this row"). Drives the border accent direction AND the
  // `newIndex` math in `onDrop`.
  const [dropSide, setDropSide] = useState<'before' | 'after' | null>(null)

  // Mirror `draggedIndex` in a ref so event handlers can read the latest
  // value synchronously without having to rebind when state changes.
  const draggedIndexRef = useRef<number | null>(null)
  // rAF handle for the deferred drag-start visual state update (see handleDragStart).
  const dragStartRafRef = useRef<number | null>(null)
  const cancelDeferredDragStart = useCallback(() => {
    if (dragStartRafRef.current !== null && hasWindow()) {
      getWindow().cancelAnimationFrame(dragStartRafRef.current)
    }
    dragStartRafRef.current = null
  }, [])
  const setDraggedIndex = useCallback(
    (next: number | null) => {
      // A reset (or any explicit set) must win over a still-pending deferred
      // drag-start frame, otherwise a late rAF could resurrect a stale index.
      cancelDeferredDragStart()
      draggedIndexRef.current = next
      setDraggedIndexState(next)
    },
    [cancelDeferredDragStart]
  )

  // Stable refs to values that handlers need but should not re-bind on
  const itemsRef = useRef(items)
  itemsRef.current = items
  const getItemKeyRef = useRef(getItemKey)
  getItemKeyRef.current = getItemKey
  const onSortRef = useRef(onSort)
  onSortRef.current = onSort
  const animationMsRef = useRef(animationMs)
  animationMsRef.current = animationMs

  // Element ref map: key -> HTMLElement (for FLIP measurement)
  const elementMapRef = useRef<Map<string, HTMLElement>>(new Map())
  // Position cache: key -> last known rect (the "First" snapshot)
  const rectCacheRef = useRef<Map<string, DOMRect>>(new Map())
  // Active FLIP animation bookkeeping so we can cancel / cleanup
  const pendingRafRef = useRef<number | null>(null)
  const pendingTimeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set())
  const animatedElementsRef = useRef<Set<HTMLElement>>(new Set())

  const itemKeySequence = useMemo(() => items.map((item, index) => getItemKey(item, index)).join('\u0001'), [items, getItemKey])

  const clearInFlightAnimations = useCallback(() => {
    if (pendingRafRef.current !== null && hasWindow()) {
      getWindow().cancelAnimationFrame(pendingRafRef.current)
    }
    pendingRafRef.current = null
    pendingTimeoutsRef.current.forEach((id) => clearTimeout(id))
    pendingTimeoutsRef.current.clear()
    animatedElementsRef.current.forEach((el) => {
      el.style.transform = ''
      el.style.transition = ''
    })
    animatedElementsRef.current.clear()
  }, [])

  const prefersReducedMotion = useCallback((): boolean => {
    if (!hasWindow()) {
      return false
    }
    const mediaQuery = getWindow().matchMedia?.('(prefers-reduced-motion: reduce)')
    return !!mediaQuery?.matches
  }, [])

  const captureFirstSnapshot = useCallback(() => {
    const cache = new Map<string, DOMRect>()
    elementMapRef.current.forEach((el, key) => {
      cache.set(key, el.getBoundingClientRect())
    })
    rectCacheRef.current = cache
  }, [])

  // FLIP "Last → Invert → Play" pass runs whenever the item key sequence changes.
  useLayoutEffect(() => {
    const currentAnimationMs = animationMsRef.current
    if (currentAnimationMs === 0) {
      rectCacheRef.current = new Map()
      return
    }
    if (prefersReducedMotion()) {
      rectCacheRef.current = new Map()
      return
    }

    const prev = rectCacheRef.current
    if (prev.size === 0) {
      return
    }

    // Last: measure current rects keyed by the same keys.
    const next = new Map<string, DOMRect>()
    elementMapRef.current.forEach((el, key) => {
      next.set(key, el.getBoundingClientRect())
    })

    const deltas = computeFlipDeltas(prev, next)
    if (deltas.size === 0) {
      rectCacheRef.current = new Map()
      return
    }

    // Invert — synchronously move each element back to its pre-reorder spot.
    const invertedElements: HTMLElement[] = []
    deltas.forEach((delta, key) => {
      const el = elementMapRef.current.get(key)
      if (!el) {
        return
      }
      el.style.transition = 'none'
      el.style.transform = `translate(${delta.dx}px, ${delta.dy}px)`
      invertedElements.push(el)
      animatedElementsRef.current.add(el)
    })

    // Play — on next frame, clear the transform under a transition.
    if (hasWindow() && !isEmpty(invertedElements)) {
      pendingRafRef.current = getWindow().requestAnimationFrame(() => {
        pendingRafRef.current = null
        invertedElements.forEach((el) => {
          el.style.transition = `transform ${currentAnimationMs}ms ease`
          el.style.transform = ''
        })
        const timeoutId = setTimeout(() => {
          pendingTimeoutsRef.current.delete(timeoutId)
          invertedElements.forEach((el) => {
            el.style.transform = ''
            el.style.transition = ''
            animatedElementsRef.current.delete(el)
          })
        }, currentAnimationMs)
        pendingTimeoutsRef.current.add(timeoutId)
      })
    }

    // Consume the snapshot so a subsequent unrelated re-render doesn't re-animate.
    rectCacheRef.current = new Map()
  }, [itemKeySequence, prefersReducedMotion])

  // Cleanup on unmount: clear caches, cancel pending work, strip inline styles.
  useEffect(() => {
    return () => {
      clearInFlightAnimations()
      cancelDeferredDragStart()
      elementMapRef.current.clear()
      rectCacheRef.current = new Map()
    }
  }, [clearInFlightAnimations, cancelDeferredDragStart])

  // Ref callback — manages the key → element map used for FLIP measurement.
  // The index does NOT flow through this ref anymore; it is carried via the
  // `data-drag-sort-index` attribute on each item / handle element instead.
  const makeRef = useCallback(
    (key: string) => (el: HTMLElement | null) => {
      if (el) {
        elementMapRef.current.set(key, el)
      } else {
        elementMapRef.current.delete(key)
      }
    },
    []
  )

  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      const index = readIndexFromEvent(e)
      if (index === null) {
        return
      }

      // Override the default drag image (which would be just the handle
      // element) with the host element, so the user sees the full
      // row / card being dragged — matching the old react-sortablejs UX.
      // When the host lookup fails (edge case), fall back to the browser's
      // default (handle-only) ghost. For ColumnList, host === handle so
      // this is a no-op. `elementMapRef` is populated by the `getItemProps`
      // `ref` callback and keyed by `getItemKey`.
      const item = itemsRef.current[index]
      if (item !== undefined && item !== null) {
        const key = getItemKeyRef.current(item, index)
        const hostEl = elementMapRef.current.get(key)
        if (hostEl) {
          const hostRect = hostEl.getBoundingClientRect()
          const offsetX = e.clientX - hostRect.left
          const offsetY = e.clientY - hostRect.top
          e.dataTransfer.setDragImage(hostEl, offsetX, offsetY)
        }
      }

      e.dataTransfer.effectAllowed = 'move'

      // Set the source index on the ref SYNCHRONOUSLY so drop math reads it
      // immediately, but DEFER the React state update to the next frame.
      // Updating state synchronously inside `dragstart` re-renders the dragged
      // row mid-gesture; Chromium treats the drag source mutating and ABORTS the
      // physical drag (the user sees the handle fail to grab). Dispatched
      // DragEvents are immune, so it only reproduces with a real mouse. Deferring
      // lets the native drag fully engage before any re-render. The drop / FLIP
      // logic reads `draggedIndexRef`, so it is unaffected by the deferral.
      draggedIndexRef.current = index
      cancelDeferredDragStart()
      if (hasWindow()) {
        dragStartRafRef.current = getWindow().requestAnimationFrame(() => {
          dragStartRafRef.current = null
          setDraggedIndexState(index)
          setDropTargetIndex(index)
        })
      } else {
        setDraggedIndexState(index)
        setDropTargetIndex(index)
      }
    },
    [cancelDeferredDragStart]
  )

  // Compute which half of the target element the cursor is over. 'before'
  // means top half ("insert above"), 'after' means bottom half ("insert
  // below"). Returns 'before' when the rect has zero height (JSDOM / detached
  // elements) so tests get deterministic behavior.
  const computeDropSide = (e: React.DragEvent<HTMLElement>): 'before' | 'after' => {
    const rect = e.currentTarget.getBoundingClientRect()
    if (rect.height === 0) {
      return 'before'
    }
    return e.clientY > rect.top + rect.height / 2 ? 'after' : 'before'
  }

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLElement>) => {
    const index = readIndexFromEvent(e)
    if (index === null) {
      return
    }
    // Stop propagation so the container-level `handleContainerDragOver`
    // doesn't fire on bubbled events and overwrite `dropTargetIndex` with
    // `items.length - 1`. The container handler should only run for drops
    // that miss every item (overshoot case).
    e.stopPropagation()
    setDropTargetIndex(index)
    setDropSide(computeDropSide(e))
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = 'move'
    const index = readIndexFromEvent(e)
    if (index !== null) {
      setDropTargetIndex(index)
      setDropSide(computeDropSide(e))
    }
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault()
      // Prevent the container-level drop handler from also firing. When a
      // drop lands on an actual item, the item owns it — the container
      // fallback only runs for overshoot drops in container dead space.
      e.stopPropagation()

      const oldIndex = draggedIndexRef.current
      const hovered = readIndexFromEvent(e)
      const itemCount = itemsRef.current.length

      if (oldIndex === null || hovered === null) {
        setDraggedIndex(null)
        setDropTargetIndex(null)
        setDropSide(null)
        return
      }

      // Compute effective newIndex from drop side. "Insert before" and
      // "insert after" are translated to the post-splice-remove insertion
      // index so consumers can use the standard `splice(old,1); splice(new,0,moved)`
      // pattern and land the moved item in the intuitive position.
      const side = computeDropSide(e)
      const newIndex =
        side === 'before'
          ? hovered > oldIndex
            ? hovered - 1
            : hovered
          : hovered < oldIndex
            ? hovered + 1
            : hovered

      const inBounds = oldIndex >= 0 && oldIndex < itemCount && newIndex >= 0 && newIndex < itemCount
      if (!inBounds || oldIndex === newIndex) {
        setDraggedIndex(null)
        setDropTargetIndex(null)
        setDropSide(null)
        return
      }

      // FLIP "First" — snapshot current rects BEFORE the caller's onSort
      // fires and React re-renders the new order.
      const currentAnimationMs = animationMsRef.current
      if (currentAnimationMs > 0 && !prefersReducedMotion()) {
        captureFirstSnapshot()
      }

      onSortRef.current({ oldIndex, newIndex })
      setDraggedIndex(null)
      setDropTargetIndex(null)
      setDropSide(null)
    },
    [captureFirstSnapshot, prefersReducedMotion, setDraggedIndex]
  )

  // Container-level drop handlers — fire only when a drop misses every
  // item (typical case: user overshoots past the last row / card). The
  // effective drop target is always the last valid index; splice's
  // natural clamping in caller `onSort` handlers produces an append.
  const handleContainerDragOver = useCallback((e: React.DragEvent<HTMLElement>) => {
    if (draggedIndexRef.current === null) {
      return
    }
    // Defensive guard: item-level handlers already call `stopPropagation`
    // to prevent bubbling, but if `e.target` is still inside an item (edge
    // cases from event-target quirks, cell padding, etc.), bail out so we
    // don't overwrite the item's `dropTargetIndex`.
    const target = e.target as HTMLElement | null
    if (target?.closest?.('[data-drag-sort-index]')) {
      return
    }
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    const lastIndex = itemsRef.current.length - 1
    if (lastIndex >= 0) {
      setDropTargetIndex(lastIndex)
      // Overshoot drops are always "insert after the last row" → the last
      // item gets a bottom-edge accent, matching user intent at end of list.
      setDropSide('after')
    }
  }, [])

  // Clear stale `dropTargetIndex` / `dropSide` state when the cursor leaves
  // the container entirely (not just moves between child items). Without
  // this, the indicator can get stuck on the last hovered row when the
  // user moves their cursor out of the table / grid area without dropping.
  const handleContainerDragLeave = useCallback((e: React.DragEvent<HTMLElement>) => {
    if (draggedIndexRef.current === null) {
      return
    }
    // Standard guard: `e.relatedTarget` is the element the cursor is
    // entering. If it's null (left the viewport / window) or not a
    // descendant of the container, the drag has genuinely left.
    const related = e.relatedTarget as Node | null
    if (related && e.currentTarget.contains(related)) {
      return
    }
    setDropTargetIndex(null)
    setDropSide(null)
  }, [])

  const handleContainerDrop = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault()

      const oldIndex = draggedIndexRef.current
      const itemCount = itemsRef.current.length
      const newIndex = itemCount - 1

      if (oldIndex === null || newIndex < 0) {
        setDraggedIndex(null)
        setDropTargetIndex(null)
        setDropSide(null)
        return
      }
      if (oldIndex === newIndex) {
        setDraggedIndex(null)
        setDropTargetIndex(null)
        setDropSide(null)
        return
      }

      const currentAnimationMs = animationMsRef.current
      if (currentAnimationMs > 0 && !prefersReducedMotion()) {
        captureFirstSnapshot()
      }

      onSortRef.current({ oldIndex, newIndex })
      setDraggedIndex(null)
      setDropTargetIndex(null)
      setDropSide(null)
    },
    [captureFirstSnapshot, prefersReducedMotion, setDraggedIndex]
  )

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null)
    setDropTargetIndex(null)
    setDropSide(null)
  }, [setDraggedIndex])

  const getItemProps = useCallback(
    (index: number): DragSortItemProps => {
      if (disabled) {
        return {
          ref: noOpRef,
          'data-drag-sort-index': index,
          onDragEnter: noOpHandler,
          onDragOver: noOpHandler,
          onDrop: noOpHandler,
        }
      }

      const item = itemsRef.current[index]
      const key =
        item !== undefined && item !== null
          ? getItemKeyRef.current(item, index)
          : `__drag-sort-fallback-${index}`

      const isDragging = draggedIndex === index
      const isDropTarget = dropTargetIndex === index && draggedIndex !== null && draggedIndex !== index

      const result: DragSortItemProps = {
        ref: makeRef(key),
        'data-drag-sort-index': index,
        onDragEnter: handleDragEnter,
        onDragOver: handleDragOver,
        onDrop: handleDrop,
      }
      if (isDragging) {
        result['data-dragging'] = 'true'
      }
      if (isDropTarget) {
        result['data-drop-target'] = 'true'
        if (dropSide) {
          result['data-drop-side'] = dropSide
        }
      }
      return result
    },
    [disabled, draggedIndex, dropTargetIndex, dropSide, handleDragEnter, handleDragOver, handleDrop, makeRef]
  )

  const getHandleProps = useCallback(
    (index: number): DragSortHandleProps => {
      if (disabled) {
        return {
          draggable: false,
          'data-drag-sort-index': index,
          onDragStart: noOpHandler,
          onDragEnd: noOpHandler,
        }
      }
      return {
        draggable: true,
        'data-drag-sort-index': index,
        onDragStart: handleDragStart,
        onDragEnd: handleDragEnd,
      }
    },
    [disabled, handleDragEnd, handleDragStart]
  )

  const getContainerProps = useCallback((): DragSortContainerProps => {
    if (disabled) {
      return { onDragOver: noOpHandler, onDragLeave: noOpHandler, onDrop: noOpHandler }
    }
    return {
      onDragOver: handleContainerDragOver,
      onDragLeave: handleContainerDragLeave,
      onDrop: handleContainerDrop,
    }
  }, [disabled, handleContainerDragOver, handleContainerDragLeave, handleContainerDrop])

  return {
    getItemProps,
    getHandleProps,
    getContainerProps,
    dragState: {
      draggedIndex,
      dropTargetIndex,
      dropSide,
      isDragging: draggedIndex !== null,
    },
  }
}
